import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AuthService, GetTokenSilentlyOptions } from '@auth0/auth0-angular';
import { switchMap, catchError, take } from 'rxjs/operators';
import { environment } from '../../../assets/env/environment';
import { SessionExpiredError, SessionService, isSessionEnded } from '../session/session.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly publicEndpoints = [
    '/api/v1/payment/products'
  ];

  constructor(private auth: AuthService, private session: SessionService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.startsWith(environment.API_BASE_URL)) {
      return next.handle(req);
    }

    if (this.isPublicEndpoint(req.url)) {
      return next.handle(req);
    }

    // DOCU-1069: `take(1)` ist hier nicht Kosmetik. `isAuthenticated$` ist ein
    // langlebiger Stream des Auth0-SDK, der nie abschliesst — ohne `take(1)`
    // schliesst auch der zurueckgegebene HTTP-Stream nie ab. Die Antwort kommt
    // zwar an (`next`), aber `complete` bleibt aus. Alles, was darauf wartet,
    // haengt still: `forkJoin` ueber mehrere Requests feuert seinen Callback nie.
    // Aufgefallen beim Umsortieren von Eintraegen (DOCU-949) — dort blieb der
    // Ladezustand stehen, Liste und Anfasser blieben deaktiviert und ein zweites
    // Verschieben war erst nach dem Neuladen moeglich. Fuer einen Request wird
    // genau ein Auth-Status gebraucht.
    return this.auth.isAuthenticated$.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return next.handle(req);
        }

        // DOCU-1323: Ohne frischen Token wird nicht gesendet. Frueher ging der
        // Request dann mit dem beim Login gespeicherten Token raus — nach 30
        // Tagen (Refresh-Token-Laufzeit) abgelaufen, 401, Abmeldung mitten in
        // der Eingabe.
        return this.accessToken().pipe(
          switchMap(token => next.handle(this.withToken(req, token))),
          catchError(error => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 401 || error instanceof SessionExpiredError) {
              return throwError(() => error);
            }
            // Einmal am Cache vorbei erneuern und wiederholen. Erst wenn auch
            // das 401 liefert, meldet HttpErrorInterceptor ab.
            return this.accessToken({ cacheMode: 'off' }).pipe(
              switchMap(token => next.handle(this.withToken(req, token)))
            );
          })
        );
      })
    );
  }

  private accessToken(options?: GetTokenSilentlyOptions): Observable<string> {
    return this.auth.getAccessTokenSilently(options).pipe(
      catchError(error => {
        console.error('Token retrieval failed:', error);
        return isSessionEnded(error) ? this.session.reauthenticate() : throwError(() => error);
      })
    );
  }

  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
  }

  private isPublicEndpoint(url: string): boolean {
    return this.publicEndpoints.some(endpoint => url.includes(endpoint));
  }
}
